import { getLocalePluralText } from "shared/boot/i18n";
import ModalService from "shared/modals/ModalService";
import {
  type InstantInsightsReport,
  type UserAccessible,
} from "shared/resources";
import InstantInsightsReportService from "shared/services/api/InstantInsightsReportService";
import { type Nullable } from "shared/types";

export interface ShareInstantInsightsReportModalResponse {
  names: string;
  userAccessibles: UserAccessible[];
}

export function useInsightsShareReport() {
  function selectUsersToShareWith(
    existingUsers: UserAccessible[] = [],
    dashboard?: InstantInsightsReport
  ): Promise<Nullable<ShareInstantInsightsReportModalResponse>> {
    return new Promise((resolve) => {
      ModalService.open("ShareInstantInsightsReportModal", {
        events: {
          cancel: () => {
            resolve(null);
          },
          confirm: (userAccessibles: UserAccessible[], names: string) => {
            resolve({ names, userAccessibles });
          },
        },
        props: {
          dashboard,
          sharedWithUsers: existingUsers,
        },
      });
    });
  }

  async function shareInsightsReport(
    dashboard: InstantInsightsReport
  ): Promise<Nullable<string>> {
    const response = await selectUsersToShareWith(
      dashboard.userAccessibles,
      dashboard
    );

    if (!response) {
      return null;
    }

    const { names: message, userAccessibles } = response;

    if (!userAccessibles.some((userAccessible) => userAccessible.isDirty())) {
      return null;
    }

    Object.assign(dashboard, { userAccessibles });

    await dashboard.saveAssociation("userAccessibles");

    return message;
  }

  async function bulkShareInsightsReports(
    ids: number[]
  ): Promise<Nullable<string>> {
    const response = await selectUsersToShareWith();

    if (!response) {
      return null;
    }

    const { userAccessibles } = response;

    await InstantInsightsReportService.bulkShare(ids, userAccessibles);

    const dashboardsSharedText = getLocalePluralText(
      "insights_dashboards.dashboards_shared",
      ids.length
    );

    const dashboardsSharedWithText = getLocalePluralText(
      "insights_dashboards.dashboards_shared_with",
      userAccessibles.length
    );

    return `${dashboardsSharedText} ${dashboardsSharedWithText}`;
  }

  return {
    bulkShareInsightsReports,
    shareInsightsReport,
  };
}

export default useInsightsShareReport;
